import api from '@/services/utils/apiInstance.ts';

import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type {
  IdentityDuplicateReview,
  IdentityDuplicateReviewFilters,
  IdentityDuplicateReviewResponseApi
} from '@/types/services/identityDuplicateReview/identityDuplicateReview.type.ts';

// Also used by userService to map the `duplicate_review` block of `GET /user/{id}`.
export function mapIdentityDuplicateReview(data: IdentityDuplicateReviewResponseApi): IdentityDuplicateReview {
  return {
    id: data.id,
    status: data.status,
    createdAt: data.created_at,
    user: {
      id: data.user.id,
      firstName: data.user.first_name,
      lastName: data.user.last_name,
      email: data.user.email
    },
    matches: (data.matches ?? []).map(match => ({
      userId: match.user_id,
      firstName: match.first_name,
      lastName: match.last_name,
      email: match.email,
      birthPlaceMatch: match.birth_place_match ?? null
    })),
    resolvedAt: data.resolved_at ?? null,
    resolvedById: data.resolved_by_id ?? null,
    resolutionNote: data.resolution_note ?? null
  }
}

class IdentityDuplicateReviewService {
  async getPaginated(
    page: number,
    limit: number,
    filters: IdentityDuplicateReviewFilters
  ): Promise<PaginatedResponse<IdentityDuplicateReview>> {
    const query = new URLSearchParams()
    query.set('page', String(page))
    query.set('limit', String(limit))
    if (filters.status) query.set('status', filters.status)
    if (filters.userId != null) query.set('userId', String(filters.userId))

    const response = await api.get(`/identity-duplicate-review/bo?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((review: IdentityDuplicateReviewResponseApi) => mapIdentityDuplicateReview(review))
    }
  }

  async get(reviewId: number): Promise<IdentityDuplicateReview> {
    const response = await api.get(`/identity-duplicate-review/bo/${reviewId}`)
    return mapIdentityDuplicateReview(response.data)
  }

  // Homonym: not a duplicate.
  async clear(reviewId: number, note: string): Promise<IdentityDuplicateReview> {
    const response = await api.post(`/identity-duplicate-review/bo/${reviewId}/clear`, { note })
    return mapIdentityDuplicateReview(response.data)
  }

  // Real duplicate: the backend deactivates the account.
  async confirm(reviewId: number, note: string): Promise<IdentityDuplicateReview> {
    const response = await api.post(`/identity-duplicate-review/bo/${reviewId}/confirm`, { note })
    return mapIdentityDuplicateReview(response.data)
  }
}

export default new IdentityDuplicateReviewService();
